// Cache du fond de carte pour la zone de travail téléchargée : tuiles vectorielles OpenFreeMap des
// emprises des missions du jour (zooms 13 à 16), bornées en nombre, écrites dans le cache
// « cc-terrain-tuiles » que le service worker sert aussi hors connexion (stratégie cache d'abord).
import type { Emprise, FondCarte, Position } from '@cc/ui';

export const CACHE_TUILES = 'cc-terrain-tuiles';
export const ZOOMS_CACHE = [13, 14, 15, 16];
/** Nombre maximal de tuiles d'un téléchargement (environ 10 Mo au plus en ville). */
export const TUILES_MAX = 240;
export const POLICES = ['Noto Sans Regular', 'Noto Sans Italic', 'Noto Sans Bold'];

export interface Tuile {
  z: number;
  x: number;
  y: number;
}

export function tuileDePosition(longitude: number, latitude: number, z: number): Tuile {
  const n = 2 ** z;
  const lat = (Math.max(-85.05, Math.min(85.05, latitude)) * Math.PI) / 180;
  const x = Math.floor(((longitude + 180) / 360) * n);
  const y = Math.floor(((1 - Math.log(Math.tan(lat) + 1 / Math.cos(lat)) / Math.PI) / 2) * n);
  return { z, x: Math.min(n - 1, Math.max(0, x)), y: Math.min(n - 1, Math.max(0, y)) };
}

/** Tuiles couvrant une emprise aux zooms donnés, du plus large au plus fin, bornées. */
export function tuilesPourEmprise(e: Emprise, zooms = ZOOMS_CACHE, max = TUILES_MAX): Tuile[] {
  const tuiles: Tuile[] = [];
  for (const z of zooms) {
    const a = tuileDePosition(e[0], e[3], z);
    const b = tuileDePosition(e[2], e[1], z);
    for (let x = a.x; x <= b.x; x += 1) {
      for (let y = a.y; y <= b.y; y += 1) {
        if (tuiles.length >= max) return tuiles;
        tuiles.push({ z, x, y });
      }
    }
  }
  return tuiles;
}

/** Emprise autour de positions, élargie d'une marge en mètres (400 m par défaut). */
export function empriseAutour(positions: Position[], margeM = 400): Emprise | null {
  if (positions.length === 0) return null;
  const lons = positions.map((p) => p[0]);
  const lats = positions.map((p) => p[1]);
  const latMoy = (Math.min(...lats) + Math.max(...lats)) / 2;
  const dLat = margeM / 111_320;
  const dLon = margeM / (111_320 * Math.max(0.2, Math.cos((latMoy * Math.PI) / 180)));
  return [Math.min(...lons) - dLon, Math.min(...lats) - dLat, Math.max(...lons) + dLon, Math.max(...lats) + dLat];
}

export function cacheDisponible(): boolean {
  return typeof window !== 'undefined' && 'caches' in window;
}

/** Gabarit d'adresse des tuiles lu dans le TileJSON du fond ; null hors connexion. */
export async function gabaritTuiles(fond: FondCarte): Promise<string | null> {
  try {
    const r = await fetch(fond.tuiles);
    if (!r.ok) return null;
    const j = (await r.json()) as { tiles?: string[] };
    return j.tiles?.[0] ?? null;
  } catch {
    return null;
  }
}

export function urlsTuiles(gabarit: string, tuiles: Tuile[]): string[] {
  return tuiles.map((t) => gabarit.replace('{z}', String(t.z)).replace('{x}', String(t.x)).replace('{y}', String(t.y)));
}

/** Glyphes des trois polices du style, plage de base (texte latin), pour les libellés hors connexion. */
export function urlsGlyphes(fond: FondCarte): string[] {
  return POLICES.map((p) => fond.glyphes.replace('{fontstack}', encodeURIComponent(p)).replace('{range}', '0-255'));
}

export interface AvancementCache {
  total: number;
  faites: number;
  deja: number;
  echecs: number;
  octets: number;
  terminee: boolean;
}

/** Télécharge les adresses absentes du cache, quatre à la fois ; s'arrête sur un signal ou hors connexion. */
export async function mettreEnCache(urls: string[], surAvancement?: (a: AvancementCache) => void, signal?: AbortSignal): Promise<AvancementCache> {
  const a: AvancementCache = { total: urls.length, faites: 0, deja: 0, echecs: 0, octets: 0, terminee: false };
  if (!cacheDisponible()) {
    a.terminee = true;
    return a;
  }
  const cache = await caches.open(CACHE_TUILES);
  const file = [...urls];
  const travailleur = async () => {
    while (file.length > 0 && !signal?.aborted) {
      const url = file.shift() as string;
      try {
        if (await cache.match(url)) {
          a.deja += 1;
        } else {
          const r = await fetch(url, { signal });
          if (!r.ok) throw new Error(`HTTP ${r.status}`);
          const taille = Number(r.headers.get('content-length') ?? 0);
          await cache.put(url, r.clone());
          a.octets += taille || (await r.blob()).size;
          a.faites += 1;
        }
      } catch {
        a.echecs += 1;
        if (signal?.aborted || (typeof navigator !== 'undefined' && navigator.onLine === false)) break;
      }
      surAvancement?.({ ...a });
    }
  };
  await Promise.all(Array.from({ length: 4 }, travailleur));
  a.terminee = true;
  surAvancement?.({ ...a });
  return a;
}

export async function etatCacheTuiles(): Promise<{ entrees: number; octets: number } | null> {
  if (!cacheDisponible()) return null;
  const cache = await caches.open(CACHE_TUILES);
  const cles = await cache.keys();
  let octets = 0;
  for (const c of cles) {
    const r = await cache.match(c);
    if (r) octets += Number(r.headers.get('content-length') ?? 0) || (await r.clone().blob()).size;
  }
  return { entrees: cles.length, octets };
}

export async function viderCacheTuiles(): Promise<void> {
  if (!cacheDisponible()) return;
  await caches.delete(CACHE_TUILES);
}
